import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeProvider } from './theme';
import { LanguageProvider } from './lib/language';
import Home from './pages/Home';
import Works from './pages/Works';
import Studio from './pages/Studio';
import Contact from './pages/Contact';
import Resume from './pages/Resume';
import StoryAssets from './pages/StoryAssets';
import Header from './components/Header';
import Footer from './components/Footer';
import ChatWidget from './components/chatbot/ChatWidget';

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

function AppContent() {
  const location = useLocation();
  const [headerRevealNudge, setHeaderRevealNudge] = useState(false);

  // Utility routes — no header, footer, or chat
  if (location.pathname === '/erick') {
    return <Resume />;
  }

  if (location.pathname === '/story-assets') {
    return <StoryAssets />;
  }

  useEffect(() => {
    let timeoutId: number | null = null;

    const handleHeaderFirstReveal = () => {
      setHeaderRevealNudge(true);
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => setHeaderRevealNudge(false), 440);
    };

    window.addEventListener('header-first-reveal', handleHeaderFirstReveal as EventListener);

    return () => {
      window.removeEventListener('header-first-reveal', handleHeaderFirstReveal as EventListener);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col">
      <Header />
      <main
        className="flex-grow"
        style={{
          transform: headerRevealNudge ? 'translateY(12px)' : 'translateY(0px)',
          transition: 'transform 440ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
            <Route path="/works" element={<PageWrapper><Works /></PageWrapper>} />
            <Route path="/works/:id" element={<PageWrapper><Works /></PageWrapper>} />
            <Route path="/studio" element={<PageWrapper><Studio /></PageWrapper>} />
            <Route path="/contact" element={<PageWrapper><Contact /></PageWrapper>} />
          </Routes>
        </AnimatePresence>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <LanguageProvider>
        <ThemeProvider showController={false}>
          <AppContent />
        </ThemeProvider>
      </LanguageProvider>
    </Router>
  );
}
